import { Fragment, useState } from 'react';
import { ParameterField, ParameterRow } from './DynamicParametersSection';
import '../../styles/reusable/CollapsibleSection.css';

interface PreviewSectionProps {
  title?: string;
  fields: ParameterField[];
  beforeData: ParameterRow[];
  afterData: ParameterRow[];
  isExecuted: boolean;
  showTimestamp?: boolean;
  defaultExpanded?: boolean;
}

/**
 * Reusable Preview Section
 * Displays before/after snapshot comparison in a table
 * Works with any field configuration
 */
const PreviewSection = ({
  title = 'Snapshot Preview',
  fields,
  beforeData,
  afterData,
  isExecuted: _isExecuted,
  showTimestamp = true,
  defaultExpanded = true,
}: PreviewSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const getDisplayValue = (value: string | number | undefined) => {
    if (value === '' || value === undefined) {
      return '-';
    }

    return value;
  };

  const isPostOnlyPreview = beforeData.length === 0 && afterData.length > 0;

  return (
    <div className="collapsible-section">
      <div className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h3>{title}</h3>
        <span className="collapse-icon">{isExpanded ? '−' : '+'}</span>
      </div>
      {isExpanded && (
        <div className="section-content">
          <div className="preview-table-wrapper">
        <table className="preview-table">
          <thead>
            <tr>
              <th>Snapshot Type</th>
              {fields.map(param => (
                <th key={param.name}>{param.label}</th>
              ))}
              {showTimestamp && <th>Timestamp</th>}
            </tr>
          </thead>
          <tbody>
            {isPostOnlyPreview
              ? afterData.map((row, index) => (
                  <Fragment key={row.id}>
                    <tr key={`before-${row.id}`} className="pre-change-row">
                      <td className="snapshot-type">
                        <span className="badge badge-warning">
                          Pre-Change {afterData.length > 1 ? index + 1 : ''}
                        </span>
                      </td>
                      {fields.map(param => (
                        <td key={param.name}>-</td>
                      ))}
                      {showTimestamp && <td>-</td>}
                    </tr>
                    <tr key={`after-${row.id}`} className="post-change-row">
                      <td className="snapshot-type">
                        <span className="badge badge-success">
                          Post-Change {afterData.length > 1 ? index + 1 : ''}
                        </span>
                      </td>
                      {fields.map(param => (
                        <td key={param.name}>{getDisplayValue(row.values[param.name])}</td>
                      ))}
                      {showTimestamp && <td>{new Date().toLocaleString()}</td>}
                    </tr>
                  </Fragment>
                ))
              : beforeData.map((row, index) => (
                  <Fragment key={row.id}>
                    <tr key={`before-${row.id}`} className="pre-change-row">
                      <td className="snapshot-type">
                        <span className="badge badge-warning">
                          Pre-Change {beforeData.length > 1 ? index + 1 : ''}
                        </span>
                      </td>
                      {fields.map(param => (
                        <td key={param.name}>{getDisplayValue(row.values[param.name])}</td>
                      ))}
                      {showTimestamp && <td>{new Date().toLocaleString()}</td>}
                    </tr>
                    <tr key={`after-${row.id}`} className="post-change-row">
                      <td className="snapshot-type">
                        <span className="badge badge-success">
                          Post-Change {beforeData.length > 1 ? index + 1 : ''}
                        </span>
                      </td>
                      {fields.map(param => (
                        <td key={param.name}>
                          {afterData[index]
                            ? getDisplayValue(afterData[index].values[param.name])
                            : '-'}
                        </td>
                      ))}
                      {showTimestamp && (
                        <td>
                          {afterData[index]
                            ? new Date().toLocaleString()
                            : '-'}
                        </td>
                      )}
                    </tr>
                  </Fragment>
                ))}
          </tbody>
        </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewSection;
